/** Current-format state values. Tagged containers make sentinel collisions
 * impossible and preserve undefined, nonfinite numbers, -0 and sparse arrays.
 * Functions, shared memory and executable/custom objects are not stored. */
export type StateValue =
  | null
  | boolean
  | number
  | string
  | { tag: 'undefined' | 'hole' }
  | { tag: 'number'; value: 'NaN' | 'Infinity' | '-Infinity' | '-0' }
  | { tag: 'bigint'; value: string }
  | { tag: 'array'; value: StateValue[] }
  | { tag: 'object'; value: [string, StateValue][] };
const MAX_DEPTH = 64,
  MAX_MEMBERS = 100_000,
  MAX_TEXT = 1_048_576;
export interface StateValueBudget {
  members: number;
  text: number;
}
const MAX_TOTAL_MEMBERS = 2_000_000,
  MAX_TOTAL_TEXT = 16 * 1024 * 1024;

export function encodeStateValue(
  root: unknown,
  budget: StateValueBudget = { members: 0, text: 0 },
  canonical = false,
  validateOnly = false,
): StateValue {
  const ancestors = new Set<object>();
  let members = 0,
    text = 0;
  const string = (value: string) => {
    text += value.length;
    budget.text += value.length;
    if (text > MAX_TEXT || budget.text > MAX_TOTAL_TEXT)
      throw new RangeError('Snapshot state text exceeds limit');
    return value;
  };
  const visit = (value: unknown, depth: number): StateValue => {
    if (
      ++members > MAX_MEMBERS ||
      ++budget.members > MAX_TOTAL_MEMBERS ||
      depth > MAX_DEPTH
    )
      throw new RangeError('Snapshot state exceeds limit');
    if (value === null || typeof value === 'boolean') return value;
    if (typeof value === 'string') return string(value);
    if (typeof value === 'number') {
      if (Object.is(value, -0)) return { tag: 'number', value: '-0' };
      return Number.isFinite(value)
        ? value
        : { tag: 'number', value: String(value) as 'NaN' | 'Infinity' | '-Infinity' };
    }
    if (value === undefined) return { tag: 'undefined' };
    if (typeof value === 'bigint') return { tag: 'bigint', value: string(String(value)) };
    if (typeof value !== 'object')
      throw new TypeError('Unsupported snapshot state value');
    const array = Array.isArray(value),
      prototype = Object.getPrototypeOf(value);
    if (
      array
        ? prototype !== Array.prototype
        : prototype !== Object.prototype && prototype !== null
    )
      throw new TypeError('Snapshot state must contain plain data');
    if (ancestors.has(value)) throw new TypeError('Circular snapshot state');
    if (
      array &&
      (members + value.length > MAX_MEMBERS ||
        budget.members + value.length > MAX_TOTAL_MEMBERS)
    )
      throw new RangeError('Snapshot state exceeds limit');
    ancestors.add(value);
    try {
      const keys = Reflect.ownKeys(value);
      for (const key of keys) {
        const property = Object.getOwnPropertyDescriptor(value, key)!;
        if (typeof key !== 'string' || !('value' in property))
          throw new TypeError('Unsupported snapshot state property');
        if (
          array &&
          key !== 'length' &&
          (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= value.length)
        )
          throw new TypeError('Unsupported array state property');
        if (!array && !property.enumerable)
          throw new TypeError('Unsupported hidden state property');
      }
      if (array) {
        if (members + value.length > MAX_MEMBERS)
          throw new RangeError('Snapshot state exceeds limit');
        const values: StateValue[] | undefined = validateOnly ? undefined : [];
        for (let i = 0; i < value.length; i++) {
          if (Object.hasOwn(value, i)) {
            const child = visit(value[i], depth + 1);
            values?.push(child);
          } else {
            if (
              ++members > MAX_MEMBERS ||
              ++budget.members > MAX_TOTAL_MEMBERS ||
              depth + 1 > MAX_DEPTH
            )
              throw new RangeError('Snapshot state exceeds limit');
            values?.push({ tag: 'hole' });
          }
        }
        return values ? { tag: 'array', value: values } : null;
      }
      const names = Object.keys(value);
      if (canonical) names.sort();
      const entries: [string, StateValue][] | undefined = validateOnly ? undefined : [];
      for (const key of names) {
        const name = string(key),
          child = visit((value as Record<string, unknown>)[key], depth + 1);
        entries?.push([name, child]);
      }
      return entries ? { tag: 'object', value: entries } : null;
    } finally {
      ancestors.delete(value);
    }
  };
  return visit(root, 0);
}

export function validateStateValue(root: unknown, budget: StateValueBudget): void {
  encodeStateValue(root, budget, false, true);
}

export function decodeStateValue(
  root: unknown,
  budget: StateValueBudget = { members: 0, text: 0 },
): unknown {
  let members = 0,
    text = 0;
  const string = (value: unknown): string => {
    if (
      typeof value !== 'string' ||
      (text += value.length) > MAX_TEXT ||
      (budget.text += value.length) > MAX_TOTAL_TEXT
    )
      throw new TypeError('Invalid snapshot state string');
    return value;
  };
  const visit = (value: any, depth: number): any => {
    if (
      ++members > MAX_MEMBERS ||
      ++budget.members > MAX_TOTAL_MEMBERS ||
      depth > MAX_DEPTH
    )
      throw new RangeError('Snapshot state exceeds limit');
    if (value === null || typeof value === 'boolean') return value;
    if (typeof value === 'string') return string(value);
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (
      !value ||
      typeof value !== 'object' ||
      Array.isArray(value) ||
      typeof value.tag !== 'string'
    )
      throw new TypeError('Invalid snapshot state tag');
    if (value.tag === 'undefined' && Object.keys(value).length === 1) return undefined;
    if (Object.keys(value).length !== 2 || !Object.hasOwn(value, 'value'))
      throw new TypeError('Invalid snapshot state fields');
    switch (value.tag) {
      case 'number':
        if (!['NaN', 'Infinity', '-Infinity', '-0'].includes(value.value))
          throw new TypeError('Invalid snapshot number');
        return Number(value.value);
      case 'bigint': {
        const spelling = string(value.value);
        if (!/^-?(0|[1-9]\d*)$/.test(spelling))
          throw new TypeError('Invalid snapshot bigint');
        return BigInt(spelling);
      }
      case 'array': {
        if (
          !Array.isArray(value.value) ||
          members + value.value.length > MAX_MEMBERS ||
          budget.members + value.value.length > MAX_TOTAL_MEMBERS
        )
          throw new TypeError('Invalid snapshot array');
        const array = new Array(value.value.length);
        for (let i = 0; i < value.value.length; i++) {
          const item = value.value[i];
          if (
            item &&
            typeof item === 'object' &&
            item.tag === 'hole' &&
            Object.keys(item).length === 1
          ) {
            if (
              ++members > MAX_MEMBERS ||
              ++budget.members > MAX_TOTAL_MEMBERS ||
              depth + 1 > MAX_DEPTH
            )
              throw new RangeError('Snapshot state exceeds limit');
            continue;
          }
          array[i] = visit(item, depth + 1);
        }
        return array;
      }
      case 'object': {
        if (!Array.isArray(value.value) || members + value.value.length > MAX_MEMBERS)
          throw new TypeError('Invalid snapshot object');
        const object: Record<string, unknown> = {};
        for (const pair of value.value) {
          if (!Array.isArray(pair) || pair.length !== 2)
            throw new TypeError('Invalid snapshot property');
          const key = string(pair[0]);
          if (Object.hasOwn(object, key))
            throw new TypeError('Duplicate snapshot property');
          Object.defineProperty(object, key, {
            value: visit(pair[1], depth + 1),
            enumerable: true,
            configurable: true,
            writable: true,
          });
        }
        return object;
      }
      default:
        throw new TypeError('Unknown snapshot state tag');
    }
  };
  return visit(root, 0);
}
